import { useEffect, useState } from 'react';
import { AlertTriangle, X, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import api from '../utils/api';

export default function MaintenanceReminderModal({ user }) {
  const [reminders, setReminders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMaintenance();
  }, []);

  const checkMaintenance = async () => {
    try {
      const response = await api.get('/vehicle-maintenance/check');
      if (response.data.reminders && response.data.reminders.length > 0) {
        setReminders(response.data.reminders);
        setShowModal(true);
        
        // Create notifications for these reminders
        await api.post('/vehicle-maintenance/create-notifications');
      }
    } catch (error) {
      console.error('Failed to check maintenance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'bg-red-600 text-white';
    if (daysUntil === 0) return 'bg-red-500 text-white';
    if (daysUntil <= 2) return 'bg-orange-500 text-white';
    if (daysUntil <= 7) return 'bg-orange-400 text-white';
    if (daysUntil <= 14) return 'bg-amber-400 text-white';
    return 'bg-blue-400 text-white';
  };

  const getUrgencyText = (daysUntil, urgency, isOverdue) => {
    if (isOverdue) return '⚠️ OVERDUE!';
    if (daysUntil === 0) return '🚨 DUE TODAY!';
    if (daysUntil <= 2) return `⚠️ Due in ${urgency}`;
    return `Due in ${urgency}`;
  };

  if (loading || !showModal || reminders.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Vehicle Maintenance Alerts</h2>
              <p className="text-sm text-slate-600">{reminders.length} vehicle(s) need attention</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowModal(false)}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <CardContent className="p-4 space-y-3">
          {reminders.map((reminder, index) => (
            <Card
              key={index}
              className={`border-l-4 ${
                reminder.is_overdue || reminder.days_until === 0
                  ? 'border-l-red-600 bg-red-50'
                  : reminder.days_until <= 7
                  ? 'border-l-orange-500 bg-orange-50'
                  : 'border-l-amber-500 bg-amber-50'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Wrench className="h-5 w-5 text-slate-700" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900">{reminder.registration}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full font-bold ${getUrgencyColor(reminder.days_until)}`}>
                          {getUrgencyText(reminder.days_until, reminder.urgency, reminder.is_overdue)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 font-medium mb-1">
                        {reminder.type} maintenance required
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-600">
                        <span>
                          Due Date: <span className="font-semibold">{new Date(reminder.due_date).toLocaleDateString('en-GB')}</span>
                        </span>
                        {!reminder.is_overdue && reminder.days_until > 0 && (
                          <span>
                            Days remaining: <span className="font-semibold">{reminder.days_until}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="pt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                window.location.href = '/vehicles';
              }}
            >
              View Vehicles
            </Button>
            <Button onClick={() => setShowModal(false)}>
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
